import { Temporal } from '../../../Temporal';
import { functionFrom, FunctionLibrary, getFunctionSignature } from '../FunctionDefinition';
import { TYPES } from '../Types';

const nowAsDateTime = functionFrom(
  'now',
  [] as const,
  TYPES.zonedDateTime,
  (ctx) => ctx.now(),
  'Current zoned date-time as configured by `now()` in the evaluator context.',
);

const plainDateFromZonedDateTime = functionFrom(
  'plainDate',
  [{ name: 'input', type: TYPES.zonedDateTime }] as const,
  TYPES.plainDate,
  (_ctx, a) => a.toPlainDate(),
  'Extracts the plain date (YYYY-MM-DD) portion from a ZonedDateTime.',
);

const plainDateFromString = functionFrom(
  'plainDate',
  [{ name: 'input', type: TYPES.string }] as const,
  TYPES.plainDate,
  (_ctx, a) => Temporal.PlainDate.from(a),
  'Parses an ISO date string (YYYY-MM-DD) into a PlainDate.',
);

const plainTimeFromZonedDateTime = functionFrom(
  'plainTime',
  [{ name: 'input', type: TYPES.zonedDateTime }] as const,
  TYPES.plainTime,
  (_ctx, a) => a.toPlainTime(),
  'Extracts the time-of-day portion from a ZonedDateTime.',
);

const plainTimeFromString = functionFrom(
  'plainTime',
  [{ name: 'input', type: TYPES.string }] as const,
  TYPES.plainTime,
  (_ctx, a) => Temporal.PlainTime.from(a),
  'Parses an ISO time string (HH:mm[:ss]) into a PlainTime.',
);

const zonedDateTimeFromString = functionFrom(
  'zonedDateTime',
  [{ name: 'input', type: TYPES.string }] as const,
  TYPES.zonedDateTime,
  (_ctx, a) => Temporal.ZonedDateTime.from(a),
  'Parses an ISO date-time string with zone into a ZonedDateTime.',
);

const zonedDateTimeFromPlainDate = functionFrom(
  'zonedDateTime',
  [{ name: 'input', type: TYPES.plainDate }] as const,
  TYPES.zonedDateTime,
  (_ctx, a) => a.toZonedDateTime(_ctx.workflowTimeZone),
  'Converts a PlainDate to a ZonedDateTime at midnight in the workflow timezone.',
);

const durationFromString = functionFrom(
  'duration',
  [{ name: 'input', type: TYPES.string }] as const,
  TYPES.duration,
  (_ctx, a) => Temporal.Duration.from(a),
  'Parses an ISO duration string (e.g. P1D) into a Duration.',
);

const addPlainDate = functionFrom(
  'add',
  [
    { name: 'date', type: TYPES.plainDate },
    { name: 'duration', type: TYPES.duration },
  ] as const,
  TYPES.plainDate,
  (_ctx, a, b) => a.add(b),
  'Adds a Duration to a PlainDate.',
);
const addZonedDateTime = functionFrom(
  'add',
  [
    { name: 'dateTime', type: TYPES.zonedDateTime },
    { name: 'duration', type: TYPES.duration },
  ] as const,
  TYPES.zonedDateTime,
  (_ctx, a, b) => a.add(b),
  'Adds a Duration to a ZonedDateTime.',
);
const addPlainTime = functionFrom(
  'add',
  [
    { name: 'time', type: TYPES.plainTime },
    { name: 'duration', type: TYPES.duration },
  ] as const,
  TYPES.plainTime,
  (_ctx, a, b) => a.add(b),
  'Adds a Duration to a PlainTime.',
);
const addDuration = functionFrom(
  'add',
  [
    { name: 'duration1', type: TYPES.duration },
    { name: 'duration2', type: TYPES.duration },
  ] as const,
  TYPES.duration,
  (_ctx, a, b) => a.add(b),
  'Adds two Durations.',
);

const subtractPlainDate = functionFrom(
  'subtract',
  [
    { name: 'date', type: TYPES.plainDate },
    { name: 'duration', type: TYPES.duration },
  ] as const,
  TYPES.plainDate,
  (_ctx, a, b) => a.subtract(b),
  'Subtracts a Duration from a PlainDate.',
);

const subtractZonedDateTime = functionFrom(
  'subtract',
  [
    { name: 'date', type: TYPES.zonedDateTime },
    { name: 'duration', type: TYPES.duration },
  ] as const,
  TYPES.zonedDateTime,
  (_ctx, a, b) => a.subtract(b),
  'Subtracts a Duration from a ZonedDateTime.',
);

const subtractPlainTime = functionFrom(
  'subtract',
  [
    { name: 'dateTime', type: TYPES.plainTime },
    { name: 'duration', type: TYPES.duration },
  ] as const,
  TYPES.plainTime,
  (_ctx, a, b) => a.subtract(b),
  'Subtracts a Duration from a PlainTime.',
);

const subtractDuration = functionFrom(
  'subtract',
  [
    { name: 'duration1', type: TYPES.duration },
    { name: 'duration2', type: TYPES.duration },
  ] as const,
  TYPES.duration,
  (_ctx, a, b) => a.subtract(b),
  'Subtracts one Duration from another.',
);

const atTimeOfDayDateTime = functionFrom(
  'atTimeOfDay',
  [
    { name: 'date', type: TYPES.zonedDateTime },
    { name: 'time', type: TYPES.plainTime },
  ] as const,
  TYPES.zonedDateTime,
  (_ctx, a, b) => a.withPlainTime(b),
  'Returns a ZonedDateTime with the supplied PlainTime.',
);

const atTimeOfDayPlainDate = functionFrom(
  'atTimeOfDay',
  [
    { name: 'date', type: TYPES.plainDate },
    { name: 'time', type: TYPES.plainTime },
  ] as const,
  TYPES.zonedDateTime,
  (_ctx, a, b) => a.toZonedDateTime(_ctx.workflowTimeZone).withPlainTime(b),
  'Combines a PlainDate and PlainTime into a ZonedDateTime in the workflow timezone.',
);
export const DATES_AND_TIMES_FUNCTIONS = {
  [getFunctionSignature(nowAsDateTime)]: nowAsDateTime,
  [getFunctionSignature(addPlainDate)]: addPlainDate,
  [getFunctionSignature(addZonedDateTime)]: addZonedDateTime,
  [getFunctionSignature(addPlainTime)]: addPlainTime,
  [getFunctionSignature(addDuration)]: addDuration,
  [getFunctionSignature(subtractPlainDate)]: subtractPlainDate,
  [getFunctionSignature(subtractZonedDateTime)]: subtractZonedDateTime,
  [getFunctionSignature(subtractPlainTime)]: subtractPlainTime,
  [getFunctionSignature(subtractDuration)]: subtractDuration,
  [getFunctionSignature(atTimeOfDayDateTime)]: atTimeOfDayDateTime,
  [getFunctionSignature(atTimeOfDayPlainDate)]: atTimeOfDayPlainDate,
  [getFunctionSignature(plainDateFromZonedDateTime)]: plainDateFromZonedDateTime,
  [getFunctionSignature(plainDateFromString)]: plainDateFromString,
  [getFunctionSignature(plainTimeFromZonedDateTime)]: plainTimeFromZonedDateTime,
  [getFunctionSignature(plainTimeFromString)]: plainTimeFromString,
  [getFunctionSignature(zonedDateTimeFromString)]: zonedDateTimeFromString,
  [getFunctionSignature(zonedDateTimeFromPlainDate)]: zonedDateTimeFromPlainDate,
  [getFunctionSignature(durationFromString)]: durationFromString,
} satisfies FunctionLibrary;
